import { CONVERSATIONAL_ENDPOINTS, PROJECT_TYPES, STAGE_NAMES } from "@/constants/constants";

export const CONVERSATION_STEPS = {
  CHAT_PROMPT: "chat_prompt",
  FULL_SCREEN_CHAT: "full_screen_chat",
} as const;

export const CONVERSATION_UI_COPY = {
  PROJECT_LABEL: "PROJECT",
  PROJECT_MODAL_TITLE: "LET'S NAME YOUR WORK.",
  PROJECT_INPUT_LABEL: "PROJECT NAME",
  PROJECT_INPUT_PLACEHOLDER: "Project name",
  PROJECT_MODAL_CANCEL_LABEL: "Cancel without saving",
  PROJECT_MODAL_CONTINUE_LABEL: "Continue",
  PROMPT_INPUT_LABEL: "Prompt input",
  PROMPT_PLACEHOLDER: "Enter your prompt here",
  INITIAL_MESSAGE: "Now we're ready to create content. Drop a brief in to get started.",
  GENERATED_CONTENT_FALLBACK_MESSAGE: "I've generated the requested content.",
  LEGAL_NOTE: "Please ensure all article outputs are reviewed by Mondelez legal,",
  CREATE_PROJECT_BEFORE_SENDING_ERROR: "Create a content project before sending a chat message.",
  PROJECT_CREATE_ERROR: "We're unable to create your project right now. Please try again later.",
  ATTACHMENT_REUPLOAD_ERROR: "Unable to upload the selected PDF. Please attach the file again.",
  ATTACHMENT_UPLOAD_ERROR: "Unable to upload the selected PDF. Please try again.",
  SEND_MESSAGE_ERROR: "Unable to send your message. Please try again.",
  SEND_ATTACHMENT_BEFORE_UPLOAD_ERROR: "Unable to send the PDF before it is uploaded. Please try again.",
  GENERIC_PROJECT_TITLE: "CONTENT GENERATION",
  ADD_BUTTON_LABEL: "Add",
  SUBMIT_BUTTON_LABEL: "Submit",
  ATTACH_FILE_ARIA_LABEL: "Attach file",
  ATTACH_FILE_TITLE: "Upload file",
  SEND_MESSAGE_ARIA_LABEL: "Send message",
  SEND_MESSAGE_TITLE: "Send",
  UPLOAD_FILE_ERROR: "Only PDF files are supported.",
  COPY_BUTTON_LABEL: "Copy",
  COPIED_BUTTON_LABEL: "Copied",
  COPY_MESSAGE_ARIA_LABEL: "Copy message",
  COPIED_MESSAGE_ARIA_LABEL: "Copied message",
  COPIED_MESSAGE_ANNOUNCEMENT: "Message copied to clipboard.",
  RETRY_BUTTON_LABEL: "Retry",
  ASSET_MODAL_TITLE: "CHOOSE YOUR ASSETS",
  ASSET_MODAL_CHECKBOX_LABEL:
    "By uploading this file, you represent and warrant that you own or have obtained all necessary rights, licenses, and permissions to use the file without restriction, and that it does not infringe any third-party rights.",
  ASSET_MODAL_UPLOAD_TYPE_ERROR: "Please make sure your file is in .pdf format",
  ASSET_MODAL_UPLOAD_LIMIT_ERROR: "You can upload up to 4PDFs in a single message. Please remove some files and try again.",
  ASSET_MODAL_RIGHTS_REQUIRED_ERROR: "Please confirm usage rights before continuing.",
  ASSET_MODAL_UPLOAD_SIZE_ERROR: "Please make sure files are less than 50 MB overall",
  ASSET_MODAL_GENERIC_UPLOAD_ERROR: "Something went wrong. Please try again.",
  GTM_CONTENTGEN_LABEL: "Conversation Chat",
  GTM_STORYBOARD_OUTPOUT_LABEL: "Storyboard output",
  GTM_STORYBOARD_OUTPUT_PREVIEW: "Storyboard:Preview Modal",
  GTM_STEP_CONTENTGEN_LABEL: "Step 1",
  HTML: "Html",
  REPLACE_ALL_TEXT: "Replace all text",
  REPLACE_ALL_TEXT_ARIA_LABEL: "Replace All Text with Generated Content.",
  UPDATE_CHANGES: "Update Changes",
  BLOG_OUTPUT_LEGAL_NOTE: "Please ensure all image outputs are reviewed by Mondelez legal.",
  REGENERATE_BLOG: "Regenerate the blog",
  ERROR_OUTPUT_GENERATION: "Unable to generate HTML.",
  PREVIOUS: "Previous",
  NEXT: "Next",
  ADD_FRAME: "Add a frame",
  IMAGE_EDIT_MESSAGE: "Let's get started. Please upload or select the image you want to edit, and then describe your changes.",
  FIRST_CHAT_MESSAGE: "Initial Chat Message",
  PROJECT_DATA_LOADING: "Loading project data",
  REVERT_TO_ORIGINAL: "Revert to original",
  REVERT_TO_ORIGINAL_ARIA_LABEL: "Revert to original frame",
} as const;

export const CONTENT_GENERATION_ASSET_UPLOAD_MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024;

export type ContentGenerationAssetUploadExtension = "pdf";

const CONTENT_GENERATION_ASSET_UPLOAD_ACCEPT_BY_EXTENSION: Record<ContentGenerationAssetUploadExtension, string[]> = {
  pdf: [".pdf", "application/pdf"],
};

const CONTENT_GENERATION_ASSET_UPLOAD_LABEL_BY_EXTENSION: Record<ContentGenerationAssetUploadExtension, string> = {
  pdf: ".pdf",
};

export const buildContentGenerationAssetUploadConfig = (extensions: ContentGenerationAssetUploadExtension[] = ["pdf"]) => {
  const acceptedTypes = extensions.flatMap((extension) => CONTENT_GENERATION_ASSET_UPLOAD_ACCEPT_BY_EXTENSION[extension]);
  const extensionLabels = extensions.map((extension) => CONTENT_GENERATION_ASSET_UPLOAD_LABEL_BY_EXTENSION[extension]);
  const dragDropText = extensions.length === 1 && extensions[0] === "pdf" ? "PDF" : `a supported (${extensionLabels.join(", ")})`;

  return {
    uploadType: acceptedTypes.join(","),
    dragDropText,
  };
};

export const CONTENT_GENERATION_API = {
  PROJECT_TYPE: PROJECT_TYPES.CONTENT,
  PROJECT_CREATION_STAGE: STAGE_NAMES.CHAT_PROJECT_CREATION,
} as const;

export const STORY_BOARD_API = {
  PROJECT_TYPE: PROJECT_TYPES.STORYBOARD,
  PROJECT_CREATION_STAGE: STAGE_NAMES.CHAT_PROJECT_CREATION,
} as const;

export const EDIT_IMAGE_API = {
  PROJECT_TYPE: PROJECT_TYPES.EDIT_IMAGE,
  PROJECT_CREATION_STAGE: STAGE_NAMES.CHAT_PROJECT_CREATION,
} as const;

export type ProjectApiConfig = typeof CONTENT_GENERATION_API | typeof STORY_BOARD_API | typeof EDIT_IMAGE_API;

export const PROJECT_API_CONFIG_BY_ROUTE: Record<string, ProjectApiConfig> = {
  [CONVERSATIONAL_ENDPOINTS?.[0]]: CONTENT_GENERATION_API,
  [CONVERSATIONAL_ENDPOINTS?.[1]]: STORY_BOARD_API,
  [CONVERSATIONAL_ENDPOINTS?.[2]]: EDIT_IMAGE_API,
} as const;

export const resolveProjectApiConfig = (routeType?: string): ProjectApiConfig => {
  switch (routeType) {
    case "story-board":
    case "storyboard":
      return STORY_BOARD_API;
    case "image-editing":
    case "editImage":
      return EDIT_IMAGE_API;
    case "content-generation":
    default:
      return CONTENT_GENERATION_API;
  }
};

export const CONTENT_TYPES = {
  ARTICLE: "article",
} as const;

export const CHAT_ROLES = {
  AIDA: "AIDA",
  USER: "YOU",
} as const;

export const STORYBOARD_ROUTE = "story-board";
export const IMAGE_EDITING_ROUTE = "image-editing";

export const INITIAL_MESSAGES = [
  {
    role: CHAT_ROLES.AIDA,
    content: CONVERSATION_UI_COPY.INITIAL_MESSAGE,
    timestamp: new Date().toISOString(),
  },
];

export const CONTENT_GENERATION_CHAT_SIZES = {
  COMPOSER_MAX_TEXTAREA_HEIGHT: 64,
  COMPOSER_TEXTAREA_ROWS: 2,
  AIDA_ICON: {
    width: 16,
    height: 13,
  },
  ACTION_ICON: {
    width: 16,
    height: 16,
  },
  COMPOSER_ICON: {
    width: 14,
    height: 14,
  },
} as const;

export const attachmentClassName =
  "inline-flex max-w-full items-center rounded-[0.625rem] border border-content-generation-border bg-white px-3 py-2 text-[0.75rem] leading-4 text-content-generation-text shadow-content-generation-attachment";

export const HTML = "HTML";
export const blog_default_extension = "html";

export const BLOG_DOWNLOAD_OPTIONS = [
  {
    label: "HTML",
    id: "html",
  },
];

export const CHAT_COMPOSER_LEGAL_NOTICES_BY_ROUTE: Record<string, { message: string }> = {
  [CONVERSATIONAL_ENDPOINTS[0]]: {
    message: CONVERSATION_UI_COPY.BLOG_OUTPUT_LEGAL_NOTE,
  },
  [STORYBOARD_ROUTE]: {
    message: CONVERSATION_UI_COPY.BLOG_OUTPUT_LEGAL_NOTE,
  },
  [IMAGE_EDITING_ROUTE]: {
    message: CONVERSATION_UI_COPY.BLOG_OUTPUT_LEGAL_NOTE,
  },
} as const;

export const GENERATION_TYPE = {
  CONTENT_GENERATION: "contentGeneration",
  STORYBOARD: "storyboard",
};

export enum OUTPUT_INTENT_TYPE {
  CHAT_ONLY = "chat-only",
  CONTENT_EDITOR = "content-editor",
  STORYBOARD = "storyboard",
}

export enum MESSAGE_STATUS {
  PENDING = "pending",
  RUNNING = "running",
  FAILED = "failed",
  COMPLETED = "completed",
  SUCCESS = "success",
}

export const FETCH_ERROR_CHAT_HISTORY = "Failed to fetch chat history";
export const HISTORY_LIMIT_PER_PAGE = 50;
